import React, { useMemo, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { Snackbar, SnackbarContent, TextField, Hidden, Button, makeStyles, Grid, Typography } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';

import { FieldValidationError, ValidationHelper } from '../../services/server/ServerResponses';
import { AuthenticationService } from '../../services/AuthenticationService';
import { PageTitle } from '../../components/PageTitle';
import { useUser } from '../../utilities/useUser';
import { useQueryParams } from '../../components/useQueryParams';
import { routes } from '../../routes';
import { AuthLayout } from './AuthLayout';

const useStyles = makeStyles((theme) => ({
    textButton: {
        paddingLeft: '0px',
        paddingRight: '0px'
    },
    submitButton: {
        minWidth: '150px',
        marginBottom: '30px'
    },
    submitButtonMobile: {
        margin: '20px 0px 70px 0px'
    },
    buttonContainer: {
        padding: '30px 0px 70px 0px'
    },
    textLink: {
        textDecoration: 'none'
    },
    snackbarContent: {
        backgroundColor: theme.palette.error.dark,
    },
    snackbarContentIcon: {
        fontSize: 20,
        opacity: 0.9,
        marginRight: theme.spacing(1),
    },
    snackbarMessage: {
        display: 'flex',
        alignItems: 'center',
    }
}));

export function ResetPassword(){
    const classes = useStyles();
    const userContext = useUser();
    const user = userContext.onlineUser;
    const params = useParams<{ email: string, code: string }>()
    const query = useQueryParams();

    const code = useMemo(() => params.code, [params]);
    const redirectUrl = useMemo(() => query.get("redirect") ?? routes.app.libraryHomePage, [query]);
    
    const [disabled, setDisabled] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    
    const handleFormSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (disabled)
            return;

        if(email.trim().length === 0)
            return setErrors([{field: "Email", errors: ["Invalid Email Address"]}]);

        if(password !== confirmPassword)
            return setErrors([{field: "ConfirmPassword", errors: ["Please make sure your passwords match"]}]);

        setDisabled(true);
        const response = await AuthenticationService.resetPassword(email, password, confirmPassword, code);
        setDisabled(false);

        if(response.error) return setErrors([{ field: "", errors: ['An unknown error occurred. Please try again.'] }]);
        if(response.validation) return setErrors(response.errors);
        
        return userContext.updateUser(response.data);
    }

    if (user)
        return <Redirect to={redirectUrl} />;

    return (
        <AuthLayout>
            <PageTitle title="Sermon Maker - Reset password" />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={ValidationHelper.hasGenericError(errors)}
                >
                <SnackbarContent
                    className={classes.snackbarContent}
                    aria-describedby="client-snackbar"
                    message={
                        <span id="client-snackbar" className={classes.snackbarMessage}>
                            <ErrorIcon className={classes.snackbarContentIcon} />
                            <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getGenericErrorSummary(errors) }} />
                        </span>}
                />
            </Snackbar>
            <Grid container direction="column" justifyContent="center" alignItems="center">
                <Typography variant="h5" style={{fontSize: "1.7rem", fontWeight: 500}}>Reset Password</Typography>
            </Grid>
            <form onSubmit={e => handleFormSubmit(e)}>
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Email')}
                    required
                    autoFocus
                    fullWidth
                    id="email"
                    label="Email"
                    type="email"
                    margin="normal"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Email') }} />}
                    />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Password')}
                    required
                    fullWidth
                    value={password}
                    id="password"
                    label="Password"
                    type="password"
                    margin="normal"
                    onChange={e => setPassword(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Password') }} />}
                    />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'ConfirmPassword')}
                    required
                    fullWidth
                    id="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    margin="normal"
                    onChange={e => setConfirmPassword(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'ConfirmPassword') }} />}
                    />
                <Hidden smDown>
                    <Button variant="contained" size="medium" color="primary" type="submit" disabled={disabled} className={classes.submitButton}>Reset Password</Button>
                </Hidden>
                <Hidden mdUp>
                    <Button fullWidth variant="contained" size="medium" color="primary" type="submit" disabled={disabled} className={classes.submitButtonMobile}>Reset Password</Button>
                </Hidden>
            </form>
        </AuthLayout>
    );
}